import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Project, TacticalSpecialist, PerformanceKicker, ProjectStatus, KickerEligiblePerson } from '@/types/project';
import { RevenueType } from '@/types/crm';
import { ProjectAuditLogEntry } from '@/types/audit';
import { usePermissions } from '@/hooks/usePermissions';
import { useAuth } from '@/hooks/useAuth';
import { useProjects } from '@/contexts/ProjectContext';
import { projectAPI } from '@/utils/supabase/client';
import { ProjectHeader } from './ProjectHeader';
import { OverviewTab } from './tabs/OverviewTab';
import { TeamCostsTab } from './tabs/TeamCostsTab';
import { PerformanceBonusesTab } from './tabs/PerformanceBonusesTab';
import { HistoryTab } from './tabs/HistoryTab';
import { KICKER_TIERS, DEFAULT_VALUES, STRATEGY_PARTNERS } from './constants';
import { formatCurrency } from './utils';
import { LayoutDashboard, Users, Trophy, History } from 'lucide-react';

interface ProjectCalculatorProps {
  project?: Project | null;
  onSaveProject: (project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>, userEmail?: string) => void | Promise<void>;
  onBackToList: () => void;
  getAuditLog?: (projectId: string) => Promise<ProjectAuditLogEntry[]>;
}

export function ProjectCalculator({ project, onSaveProject, onBackToList, getAuditLog }: ProjectCalculatorProps) {
  const { canViewAuditLogs } = usePermissions();
  const { user } = useAuth();
  const { refreshProjects } = useProjects();

  // Load team members from API
  const [people, setPeople] = useState<string[]>([]);
  useEffect(() => {
    projectAPI.getTeamMembers().then(members => {
      setPeople(members.map(m => m.name));
    }).catch(err => console.error('Failed to load team members:', err));
  }, []);
  const [activeTab, setActiveTab] = useState('overview');

  // Project state
  const [projectName, setProjectName] = useState('');
  const [projectStatus, setProjectStatus] = useState<ProjectStatus>('active');
  const [revenue, setRevenue] = useState<number>(DEFAULT_VALUES.revenue);
  const [profitMargin, setProfitMargin] = useState<number>(DEFAULT_VALUES.profitMargin);
  const [operationsOverheadPercentage, setOperationsOverheadPercentage] = useState<number>(DEFAULT_VALUES.operationsOverheadPercentage);
  const [operationsCompensationPerOwner, setOperationsCompensationPerOwner] = useState<number>(DEFAULT_VALUES.operationsCompensationPerOwner);
  const [strategySponsor, setStrategySponsor] = useState('');
  const [operationsOwners, setOperationsOwners] = useState<string[]>(DEFAULT_VALUES.defaultOperationsOwners);
  const [tacticalSpecialists, setTacticalSpecialists] = useState<TacticalSpecialist[]>(DEFAULT_VALUES.defaultTacticalSpecialists);
  const [performanceKickers, setPerformanceKickers] = useState<PerformanceKicker[]>(DEFAULT_VALUES.defaultPerformanceKickers);
  const [revenueType, setRevenueType] = useState<RevenueType>('recurring');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);

  // Ad-hoc revenue state
  const [adHocMonth, setAdHocMonth] = useState(() => new Date().toISOString().substring(0, 7));
  const [adHocAmount, setAdHocAmount] = useState(0);
  const [savingAdHoc, setSavingAdHoc] = useState(false);

  // Load saved ad-hoc amount when project or month changes
  useEffect(() => {
    const saved = project?.adHocForecasts?.find(f => f.month === adHocMonth);
    setAdHocAmount(saved?.amount ?? 0);
  }, [project, adHocMonth]);

  // Get clientId from project for API calls
  const clientId = project?.clientId;

  // Function to add a new person via API
  const addPerson = async (name: string) => {
    try {
      const member = await projectAPI.createTeamMember({ name });
      setPeople(prev => [...prev, member.name]);
      return member;
    } catch (err) {
      console.error('Failed to create team member:', err);
      return null;
    }
  };

  // Handler for saving ad-hoc revenue
  const handleSaveAdHocRevenue = async () => {
    if (!clientId) return;
    if (!adHocAmount || adHocAmount <= 0) {
      toast.error('Please enter an amount');
      return;
    }
    setSavingAdHoc(true);
    try {
      await projectAPI.setAdHocRevenue(clientId, adHocMonth, adHocAmount);
      // Also update the revenue type if changed
      await projectAPI.updateClient(clientId, { revenue_type: revenueType });
      toast.success('Ad-hoc revenue saved');
      await refreshProjects();
    } catch (error) {
      console.error('Failed to save ad-hoc revenue:', error);
      toast.error('Failed to save ad-hoc revenue');
    } finally {
      setSavingAdHoc(false);
    }
  };

  // Handler for saving revenue type change
  const handleRevenueTypeChange = async (newType: RevenueType) => {
    const previousType = revenueType;
    const previousRevenue = revenue;
    setRevenueType(newType);
    // Variable and one-off projects don't have a retainer — set revenue to 0
    if (newType === 'variable' || newType === 'one_off') {
      setRevenue(0);
    }
    setHasUnsavedChanges(true);
    if (clientId) {
      try {
        const updates: Record<string, any> = { revenue_type: newType };
        if (newType === 'variable' || newType === 'one_off') {
          updates.confirmed_monthly_revenue = 0;
        }
        await projectAPI.updateClient(clientId, updates);
      } catch (error) {
        console.error('Failed to update revenue type:', error);
        setRevenueType(previousType);
        setRevenue(previousRevenue);
      }
    }
  };

  // Load project data when project prop changes
  useEffect(() => {
    if (project && project.baseTemplate) {
      setProjectName(project.projectName);
      setProjectStatus(project.status || 'active');
      // Variable projects have no retainer — always show 0
      const loadedRevenueType = project.revenueType || 'recurring';
      setRevenue(loadedRevenueType === 'variable' || loadedRevenueType === 'one_off' ? 0 : project.baseTemplate.revenue);
      setProfitMargin(project.baseTemplate.profitMargin);
      setOperationsOverheadPercentage(project.baseTemplate.operationsOverheadPercentage || DEFAULT_VALUES.operationsOverheadPercentage);
      setOperationsCompensationPerOwner(project.baseTemplate.operationsCompensationPerOwner || DEFAULT_VALUES.operationsCompensationPerOwner);
      setStrategySponsor(project.baseTemplate.strategySponsor);
      setOperationsOwners(project.baseTemplate.operationsOwners);
      setTacticalSpecialists(project.baseTemplate.tacticalSpecialists);

      // Migrate old kicker format to new format
      const migratedKickers = (project.baseTemplate.performanceKickers || []).map((kicker: any) => ({
        ...kicker,
        assignedSpecialistIds: kicker.assignedSpecialistIds
          ? kicker.assignedSpecialistIds
          : (kicker.assignedSpecialistId ? [kicker.assignedSpecialistId] : []),
        operationsOwnersEligible: kicker.operationsOwnersEligible ?? false,
        specialistSharePercentage: kicker.specialistSharePercentage ?? 10
      }));
      setPerformanceKickers(migratedKickers);

      // Load revenue type from project (comes from client)
      setRevenueType(project.revenueType || 'recurring');

      setHasUnsavedChanges(false);
    } else {
      // Reset to defaults for new project
      setProjectName('');
      setProjectStatus('active');
      setRevenue(DEFAULT_VALUES.revenue);
      setProfitMargin(DEFAULT_VALUES.profitMargin);
      setOperationsOverheadPercentage(DEFAULT_VALUES.operationsOverheadPercentage);
      setOperationsCompensationPerOwner(DEFAULT_VALUES.operationsCompensationPerOwner);
      setStrategySponsor('');
      setOperationsOwners(DEFAULT_VALUES.defaultOperationsOwners);
      setTacticalSpecialists(DEFAULT_VALUES.defaultTacticalSpecialists);
      setPerformanceKickers(DEFAULT_VALUES.defaultPerformanceKickers);
      setRevenueType('recurring');
      setHasUnsavedChanges(false);
    }
  }, [project]);

  // Track unsaved changes
  useEffect(() => {
    if (project && project.baseTemplate) {
      const hasChanges =
        projectName !== project.projectName ||
        projectStatus !== (project.status || 'active') ||
        revenue !== ((project.revenueType === 'variable' || project.revenueType === 'one_off') ? 0 : project.baseTemplate.revenue) ||
        profitMargin !== project.baseTemplate.profitMargin ||
        operationsOverheadPercentage !== (project.baseTemplate.operationsOverheadPercentage || DEFAULT_VALUES.operationsOverheadPercentage) ||
        operationsCompensationPerOwner !== (project.baseTemplate.operationsCompensationPerOwner || DEFAULT_VALUES.operationsCompensationPerOwner) ||
        strategySponsor !== project.baseTemplate.strategySponsor ||
        JSON.stringify(operationsOwners) !== JSON.stringify(project.baseTemplate.operationsOwners) ||
        JSON.stringify(tacticalSpecialists) !== JSON.stringify(project.baseTemplate.tacticalSpecialists) ||
        JSON.stringify(performanceKickers.map(({ id, name, marketingSpend, selectedTier, eligiblePeople, sharePercentage }) => ({ id, name, marketingSpend, selectedTier, eligiblePeople, sharePercentage }))) !==
        JSON.stringify((project.baseTemplate.performanceKickers || []).map(({ id, name, marketingSpend, selectedTier, eligiblePeople, sharePercentage }) => ({ id, name, marketingSpend, selectedTier, eligiblePeople, sharePercentage })));
      setHasUnsavedChanges(hasChanges);
    } else {
      const hasChanges =
        projectName !== '' ||
        projectStatus !== 'active' ||
        revenue !== DEFAULT_VALUES.revenue ||
        profitMargin !== DEFAULT_VALUES.profitMargin ||
        operationsOverheadPercentage !== DEFAULT_VALUES.operationsOverheadPercentage ||
        operationsCompensationPerOwner !== DEFAULT_VALUES.operationsCompensationPerOwner ||
        strategySponsor !== '' ||
        JSON.stringify(operationsOwners) !== JSON.stringify(DEFAULT_VALUES.defaultOperationsOwners) ||
        tacticalSpecialists.length !== DEFAULT_VALUES.defaultTacticalSpecialists.length;
      setHasUnsavedChanges(hasChanges);
    }
  }, [project, projectName, projectStatus, revenue, profitMargin, operationsOverheadPercentage, operationsCompensationPerOwner, strategySponsor, operationsOwners, tacticalSpecialists, performanceKickers]);

  // === TACTICAL SPECIALIST OPERATIONS ===
  const addTacticalSpecialist = (name?: string) => {
    const newSpecialist: TacticalSpecialist = {
      id: Math.random().toString(36).substr(2, 9),
      name: name || '',
      amount: 0,
      handlesKickers: false,
      assignedOwner: undefined
    };
    setTacticalSpecialists(prev => [...prev, newSpecialist]);
  };

  const removeTacticalSpecialist = (id: string) => {
    setTacticalSpecialists(prev => prev.filter(spec => spec.id !== id));
    setPerformanceKickers(prev => prev.map(kicker =>
      kicker.assignedSpecialistIds?.includes(id)
        ? { ...kicker, assignedSpecialistIds: kicker.assignedSpecialistIds.filter(sId => sId !== id) }
        : kicker
    ));
  };

  const updateTacticalSpecialist = (id: string, field: keyof TacticalSpecialist, value: string | number | boolean | undefined) => {
    setTacticalSpecialists(prev => prev.map(spec =>
      spec.id === id ? { ...spec, [field]: value } : spec
    ));
  };

  // === PERFORMANCE KICKER OPERATIONS ===
  const addPerformanceKicker = () => {
    const newKicker: PerformanceKicker = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      marketingSpend: 0,
      selectedTier: 'none',
      // New simplified model
      eligiblePeople: [],
      sharePercentage: 10,
      // Legacy fields (kept for backwards compatibility)
      assignedSpecialistIds: [],
      operationsOwnersEligible: false,
      specialistSharePercentage: 10
    };
    setPerformanceKickers(prev => [...prev, newKicker]);
  };

  const removePerformanceKicker = (id: string) => {
    setPerformanceKickers(prev => prev.filter(kicker => kicker.id !== id));
  };

  const updatePerformanceKicker = (id: string, field: keyof PerformanceKicker, value: any) => {
    setPerformanceKickers(prev => prev.map(kicker =>
      kicker.id === id ? { ...kicker, [field]: value } : kicker
    ));
  };

  // === CALCULATIONS ===
  const profitMarginDecimal = profitMargin / 100;
  const baseProfit = revenue * profitMarginDecimal;

  const totalOperationsCompensation = operationsOwners.length * operationsCompensationPerOwner;
  const baseOperationsCost = revenue * (operationsOverheadPercentage / 100);
  const operationsCost = baseOperationsCost + totalOperationsCompensation;

  const availableTacticalBudget = revenue - baseProfit - operationsCost;

  // Helper to resolve eligible people from new or legacy format
  const resolveEligiblePeople = (kicker: PerformanceKicker): KickerEligiblePerson[] => {
    // Use new format if available
    if (kicker.eligiblePeople && kicker.eligiblePeople.length > 0) {
      return kicker.eligiblePeople;
    }

    // Fall back to legacy format
    const eligible: KickerEligiblePerson[] = [];

    // Convert assignedSpecialistIds to eligible people
    const safeAssignedIds = Array.isArray(kicker.assignedSpecialistIds) ? kicker.assignedSpecialistIds : [];
    safeAssignedIds.forEach(specialistId => {
      const specialist = tacticalSpecialists.find(s => s.id === specialistId);
      if (specialist) {
        const name = specialist.assignedOwner || specialist.name;
        const isOwner = STRATEGY_PARTNERS.includes(name);
        if (!eligible.some(p => p.name === name)) {
          eligible.push({
            name,
            type: isOwner ? 'owner' : 'external',
            role: specialist.name
          });
        }
      }
    });

    // Add operations owners if eligible
    if (kicker.operationsOwnersEligible) {
      operationsOwners.forEach(owner => {
        if (!eligible.some(p => p.name === owner)) {
          eligible.push({
            name: owner,
            type: STRATEGY_PARTNERS.includes(owner) ? 'owner' : 'external',
            role: 'Operations'
          });
        }
      });
    }

    return eligible;
  };

  const kickerCalculations = performanceKickers.map(kicker => {
    const selectedTier = KICKER_TIERS.find(tier => tier.value === kicker.selectedTier);
    const bonus = selectedTier ? kicker.marketingSpend * selectedTier.percentage : 0;

    // Use new simplified model
    const eligiblePeople = resolveEligiblePeople(kicker);
    const totalEligibleCount = eligiblePeople.length;
    const sharePercentage = kicker.sharePercentage ?? kicker.specialistSharePercentage ?? 10;

    const perPersonPayout = bonus * (sharePercentage / 100);
    const totalSpecialistPayout = perPersonPayout * totalEligibleCount;
    const ownerPayout = bonus - totalSpecialistPayout;

    return {
      ...kicker,
      bonus,
      specialistPayout: totalSpecialistPayout,
      perPersonPayout,
      eligibleCount: totalEligibleCount,
      eligiblePeople, // Include resolved eligible people
      ownerPayout,
      tierInfo: selectedTier
    };
  });

  const totalKickerBonus = kickerCalculations.reduce((sum, calc) => sum + calc.bonus, 0);
  const totalKickerSpecialistPayouts = kickerCalculations.reduce((sum, calc) => sum + calc.specialistPayout, 0);
  const totalKickerOwnerRevenue = kickerCalculations.reduce((sum, calc) => sum + calc.ownerPayout, 0);

  const baseTacticalCosts = tacticalSpecialists.reduce((sum, spec) => sum + spec.amount, 0);
  const totalTacticalCosts = baseTacticalCosts;

  const tacticalBudgetRemaining = availableTacticalBudget - totalTacticalCosts;

  const totalProjectCosts = operationsCost + totalTacticalCosts;
  const totalRevenue = revenue + totalKickerBonus;
  const totalProfit = baseProfit + totalKickerOwnerRevenue;

  // Get specialist kicker earnings - works with both new and legacy formats
  const getSpecialistKickerEarnings = (specialistId: string) => {
    const specialist = tacticalSpecialists.find(s => s.id === specialistId);
    if (!specialist) return 0;

    const personName = specialist.assignedOwner || specialist.name;

    return kickerCalculations
      .filter(calc => {
        // Check new format first
        if (calc.eligiblePeople && calc.eligiblePeople.length > 0) {
          return calc.eligiblePeople.some(p => p.name === personName);
        }
        // Fall back to legacy format
        const safeAssignedIds = Array.isArray(calc.assignedSpecialistIds) ? calc.assignedSpecialistIds : [];
        return safeAssignedIds.includes(specialistId);
      })
      .reduce((sum, calc) => sum + (calc.perPersonPayout || 0), 0);
  };

  // === SAVE HANDLER ===
  const handleSaveProject = async () => {
    if (saving) return; // Prevent multiple submissions

    setSaving(true);
    try {
      const projectData = {
        projectName,
        status: projectStatus,
        baseTemplate: {
          revenue,
          profitMargin,
          operationsOverheadPercentage,
          operationsCompensationPerOwner,
          strategySponsor,
          operationsOwners,
          tacticalSpecialists,
          performanceKickers
        },
        monthlyOverrides: project?.monthlyOverrides || {}
      };
      await onSaveProject(projectData, user?.email);
      setHasUnsavedChanges(false);

      // If status changed to inactive or canceled, navigate back to list
      // (these statuses cause the project to be filtered out of Portfolio view)
      if (projectStatus === 'inactive' || projectStatus === 'canceled') {
        onBackToList();
      }
    } finally {
      setSaving(false);
    }
  };

  // Default audit log fetcher
  const defaultGetAuditLog = async (_projectId: string): Promise<ProjectAuditLogEntry[]> => {
    // This will be replaced with actual API call
    return [];
  };

  return (
    <div className="space-y-4">
      <ProjectHeader
        project={project}
        projectName={projectName}
        projectStatus={projectStatus}
        revenue={revenue}
        revenueType={revenueType}
        hasUnsavedChanges={hasUnsavedChanges}
        onBackToList={onBackToList}
        onSave={handleSaveProject}
        isSaveDisabled={!projectName.trim()}
        isSaving={saving}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-3 md:grid-cols-4 h-auto p-1">
          <TabsTrigger value="overview" className="flex items-center gap-1 md:gap-1.5 py-2 px-2 md:px-3">
            <LayoutDashboard className="h-4 w-4 hidden md:inline" />
            <span className="text-xs md:text-sm">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="team" className="flex items-center gap-1 md:gap-1.5 py-2 px-2 md:px-3">
            <Users className="h-4 w-4 hidden md:inline" />
            <span className="text-xs md:text-sm">Team & Costs</span>
          </TabsTrigger>
          <TabsTrigger value="bonuses" className="flex items-center gap-1 md:gap-1.5 py-2 px-2 md:px-3">
            <Trophy className="h-4 w-4 hidden md:inline" />
            <span className="text-xs md:text-sm">Bonuses</span>
            {totalKickerBonus > 0 && (
              <Badge variant="secondary" className="ml-1 hidden lg:inline-flex text-xs">
                {formatCurrency(totalKickerBonus)}
              </Badge>
            )}
          </TabsTrigger>
          {canViewAuditLogs && (
            <TabsTrigger value="history" className="flex items-center gap-1 md:gap-1.5 py-2 px-2 md:px-3">
              <History className="h-4 w-4 hidden md:inline" />
              <span className="text-xs md:text-sm">History</span>
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="overview" className="mt-4 md:mt-6">
          <OverviewTab
            revenue={revenue}
            baseProfit={baseProfit}
            profitMargin={profitMargin}
            operationsCost={operationsCost}
            totalOperationsCompensation={totalOperationsCompensation}
            operationsOverheadPercentage={operationsOverheadPercentage}
            baseOperationsCost={baseOperationsCost}
            totalTacticalCosts={totalTacticalCosts}
            tacticalBudgetRemaining={tacticalBudgetRemaining}
            totalKickerBonus={totalKickerBonus}
            totalKickerOwnerRevenue={totalKickerOwnerRevenue}
            totalKickerSpecialistPayouts={totalKickerSpecialistPayouts}
            totalRevenue={totalRevenue}
            totalProfit={totalProfit}
            totalProjectCosts={totalProjectCosts}
            tacticalSpecialists={tacticalSpecialists}
            operationsOwners={operationsOwners}
            performanceKickers={performanceKickers}
            kickerCalculations={kickerCalculations}
          />
        </TabsContent>

        <TabsContent value="team" className="mt-4">
          <TeamCostsTab
            projectName={projectName}
            setProjectName={setProjectName}
            projectStatus={projectStatus}
            setProjectStatus={setProjectStatus}
            revenue={revenue}
            setRevenue={setRevenue}
            revenueType={revenueType}
            onRevenueTypeChange={handleRevenueTypeChange}
            clientId={clientId}
            adHocMonth={adHocMonth}
            setAdHocMonth={setAdHocMonth}
            adHocAmount={adHocAmount}
            setAdHocAmount={setAdHocAmount}
            onSaveAdHocRevenue={handleSaveAdHocRevenue}
            savingAdHoc={savingAdHoc}
            profitMargin={profitMargin}
            setProfitMargin={setProfitMargin}
            operationsOverheadPercentage={operationsOverheadPercentage}
            setOperationsOverheadPercentage={setOperationsOverheadPercentage}
            baseProfit={baseProfit}
            operationsCost={operationsCost}
            availableTacticalBudget={availableTacticalBudget}
            strategySponsor={strategySponsor}
            setStrategySponsor={setStrategySponsor}
            operationsOwners={operationsOwners}
            setOperationsOwners={setOperationsOwners}
            operationsCompensationPerOwner={operationsCompensationPerOwner}
            setOperationsCompensationPerOwner={setOperationsCompensationPerOwner}
            totalOperationsCompensation={totalOperationsCompensation}
            people={people}
            addPerson={addPerson}
            tacticalSpecialists={tacticalSpecialists}
            addTacticalSpecialist={addTacticalSpecialist}
            removeTacticalSpecialist={removeTacticalSpecialist}
            updateTacticalSpecialist={updateTacticalSpecialist}
            baseTacticalCosts={baseTacticalCosts}
            tacticalBudgetRemaining={tacticalBudgetRemaining}
            getSpecialistKickerEarnings={getSpecialistKickerEarnings}
          />
        </TabsContent>

        <TabsContent value="bonuses" className="mt-4">
          <PerformanceBonusesTab
            performanceKickers={performanceKickers}
            addPerformanceKicker={addPerformanceKicker}
            removePerformanceKicker={removePerformanceKicker}
            updatePerformanceKicker={updatePerformanceKicker}
            tacticalSpecialists={tacticalSpecialists}
            operationsOwners={operationsOwners}
            kickerCalculations={kickerCalculations}
            totalKickerBonus={totalKickerBonus}
            totalKickerOwnerRevenue={totalKickerOwnerRevenue}
            totalKickerSpecialistPayouts={totalKickerSpecialistPayouts}
          />
        </TabsContent>

        {canViewAuditLogs && (
          <TabsContent value="history" className="mt-4">
            <HistoryTab
              projectId={project?.id}
              getAuditLog={getAuditLog || defaultGetAuditLog}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

// Re-export for backwards compatibility
export default ProjectCalculator;
